import { Link, useLocation, useNavigate } from 'react-router';
import { useState, useRef, useEffect, useMemo } from 'react';
import imgLogotexticon1 from "../../assets/logo-cherry-color.webp";
import { ConfirmModal } from './ConfirmModal';
import { logout as proLogoutApi } from '../../lib/professional-api';
import { logout as customerLogoutApi } from '../../lib/customer-api';
import { clearAuth } from '../../lib/auth';
import { clearCustomerAuth } from '../../lib/customer-auth';
import { useAuth } from '../../lib/use-auth';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';
import { NotificationBell } from './NotificationBell';
import {
  useUnreadMessages,
  useMessageStream,
  notifyMessagesChanged,
} from '../../lib/use-message-stream';
import { notifyNotificationsChanged } from '../../lib/use-notifications';

interface MainHeaderProps {
  // Kept for backwards compatibility with existing call sites; the
  // header now auto-detects the active role from the localStorage-backed
  // auth stores, so this prop is only used as a hint when neither store
  // is authenticated (to bias signup CTAs toward "Join as a Pro").
  isProfessional?: boolean;
}

type Role = 'pro' | 'customer' | 'public';

export function MainHeader({ isProfessional = false }: MainHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Read both auth stores (hooks can't be conditional). Whichever one
  // is authenticated wins — that's the role the header renders for.
  // If neither, we're in public mode.
  const proAuth = useAuth();
  const customerAuth = useCustomerAuth();
  const role: Role = proAuth.isAuthenticated
    ? 'pro'
    : customerAuth.isAuthenticated
      ? 'customer'
      : 'public';

  const isPublic = role === 'public';
  const isProMode = role === 'pro';
  const isCustomerMode = role === 'customer';
  const isAuthenticated = !isPublic;
  // Unread-message count surfaced on the "Messenger" nav link (replaces the
  // old standalone chat-bubble icon). Both hooks self-guard on auth, so
  // they no-op for signed-out visitors; the SSE stream still fires a toast
  // the instant a message lands, exactly as the bubble did.
  const messageVariant = isProMode ? 'pro' : 'customer';
  const { count: unreadMessages, refresh: refreshMessages } =
    useUnreadMessages(messageVariant);
  useMessageStream(
    messageVariant,
    (m) => {
      refreshMessages();
      notifyMessagesChanged();
      toast.info(m.preview ? `New message: ${m.preview}` : 'New message');
    },
    // Live in-app notifications (e.g. a new review) — refresh the bell
    // count immediately instead of waiting for its 60s poll, and toast.
    (n) => {
      notifyNotificationsChanged();
      toast.info(n.title || 'New notification');
    },
  );
  const user = isProMode ? proAuth.user : isCustomerMode ? customerAuth.user : null;
  // A pro is only "fully active" once the admin marks them APPROVED.
  // Until then we suppress dashboard-style nav links so they can't click
  // straight into pages they'll just be bounced from by the guard.
  const isPendingPro = isProMode && user?.status !== 'APPROVED';

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    user?.email ||
    '';
  const initials = (() => {
    const f = user?.first_name?.trim()?.[0] ?? '';
    const l = user?.last_name?.trim()?.[0] ?? '';
    const ini = (f + l).toUpperCase();
    if (ini) return ini;
    return user?.email?.[0]?.toUpperCase() ?? '?';
  })();

  // Route choices follow the detected role:
  //   - public  → customer auth pages (the Customer/Professional tabs on
  //     those pages let visitors switch role without a chooser screen)
  //   - logged-in customer → customer entry points
  //   - logged-in pro → pro entry points
  // The `isProfessional` prop only nudges the public-mode CTA toward the
  // pro side so role-themed marketing pages stay coherent.
  const signInPath = isProMode
    ? '/professional/signin'
    : isCustomerMode
      ? '/signin/customer'
      : '/signin/customer';
  const signUpPath = isProMode
    ? '/professional/signup'
    : isCustomerMode
      ? '/signup/customer'
      : isProfessional
        ? '/professional/signup'
        : '/signup/customer';
  const accountPath = isProMode
    ? '/professional/account-settings'
    : '/account-settings';
  // Logo click: authed pros always go to their dashboard. The route's
  // RequireOnboardedPro guard handles the edge cases (sends them to
  // /verify-identity if their identity docs are still pending, or to
  // /service-offer if onboarding isn't finished) — we don't try to
  // second-guess that here from a possibly-stale local cached status.
  const logoPath = isProMode
    ? '/professional/dashboard'
    : isCustomerMode
      ? '/user-landing'
      : '/';

  // Nav links are role-aware. Public users only see public destinations
  // — Messenger / Bookings / Dashboard require auth, so they stay hidden
  // until the user signs in.
  const navLinks = useMemo<{ name: string; path: string }[]>(() => {
    if (isProMode) {
      // Pending pros only get onboarding-related nav until an admin
      // approves them — Dashboard/Messenger/etc. are gated server-side
      // anyway and hiding them prevents pointless redirects.
      if (isPendingPro) {
        return [
          { name: 'Verify Identity', path: '/professional/verify-identity' },
          { name: 'Help Center', path: '/professional/help-center' },
        ];
      }
      return [
        { name: 'Dashboard', path: '/professional/dashboard' },
        { name: 'Messenger', path: '/professional/messenger' },
        { name: 'Services', path: '/professional/services' },
        { name: 'Client Rates', path: '/professional/client-rates' },
        { name: 'Reviews', path: '/professional/reviews' },
        { name: 'Bookings', path: '/professional/bookings' },
        { name: 'Schedule', path: '/professional/schedule' },
        { name: 'Help Center', path: '/professional/help-center' },
      ];
    }
    if (isCustomerMode) {
      return [
        { name: 'Home', path: '/user-landing' },
        { name: 'Bookings', path: '/bookings' },
        { name: 'Messenger', path: '/messenger' },
        { name: 'Gift Cards', path: '/gift-card' },
        { name: 'Help Center', path: '/help-center' },
      ];
    }
    // Public — restricted to surfaces that work without auth. Gift card
    // purchase is guest-friendly (no account needed), so it lives here.
    return [
      { name: 'Home', path: '/' },
      { name: 'About Us', path: '/about-us' },
      { name: 'Gift Cards', path: '/gift-card' },
      { name: 'Help Center', path: '/help-center' },
      { name: 'Contact', path: '/contact-us' },
    ];
  }, [isProMode, isCustomerMode, isPendingPro]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setLogoutOpen(true);
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      if (isProMode) {
        await proLogoutApi();
      } else if (isCustomerMode) {
        await customerLogoutApi();
      }
    } finally {
      if (isProMode) clearAuth();
      else if (isCustomerMode) clearCustomerAuth();
      setLoggingOut(false);
      setLogoutOpen(false);
      toast.success('Logged out.');
      navigate('/', { replace: true });
    }
  };

  const signUpLabel =
    isPublic && isProfessional ? 'Join as a Pro' : 'Sign Up';

  return (
    <header className="w-full bg-white border-b border-[#ede8e9] sticky top-0 z-50">
      <div className="max-w-[1140px] mx-auto px-4">
        <div className="flex items-center justify-between h-[80px]">
          {/* Left Side: Logo and Navigation */}
          <div className="flex items-center gap-4 md:gap-16 h-full flex-1">
            {/* Logo */}
            <Link to={logoPath} className="w-[100px] md:w-[120px] h-full flex items-center">
              <img
                src={imgLogotexticon1}
                alt="Cherry Picked"
                className="w-full h-auto object-contain"
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-8 h-full">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`relative font-['DM_Sans',sans-serif] text-sm font-bold leading-[21px] tracking-[-0.1px] transition-colors h-full flex items-center ${isActive ? 'text-[#3a3335]' : 'text-[#908e8e] hover:text-[#3a3335]'
                      }`}
                    style={{ fontVariationSettings: "'opsz' 14" }}
                  >
                    {link.name}
                    {link.path.endsWith('/messenger') && unreadMessages > 0 ? (
                      <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-[#8e2a2f] text-white text-[10px] font-bold leading-none">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    ) : null}
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#8e2a2f] rounded-t-full" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Mobile Hamburger Menu */}
          <button
            className="md:hidden flex flex-col gap-1.5 mr-4"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <span className={`w-6 h-0.5 bg-[#3a3335] transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`w-6 h-0.5 bg-[#3a3335] transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`w-6 h-0.5 bg-[#3a3335] transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>

          {/* Auth Area — avatar dropdown when signed in, Sign in / Sign up otherwise */}
          {isAuthenticated ? (
            <div className="flex items-center gap-3 relative" ref={dropdownRef}>
              {/* Messages are reached via the "Messenger" nav link (which
                  shows the unread-message badge) — no separate icon. */}
              {/* Notification bell — polls every 60s for unread count
                  via useUnreadNotifications. Routes to the role-
                  specific inbox (/inbox or /professional/inbox). */}
              <NotificationBell role={isProMode ? 'professional' : 'customer'} />
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-label="Open account menu"
                className="w-10 h-10 rounded-full overflow-hidden cursor-pointer border border-[#eee] transition-transform active:scale-95 bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[14px] flex items-center justify-center"
              >
                {initials}
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 top-[60px] w-56 bg-white border border-[#ede8e9] rounded-xl shadow-xl py-2 z-50 animate-in fade-in zoom-in duration-200">
                  <div className="px-4 py-3 border-b border-[#f0f0f0]">
                    <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                      {displayName}
                    </p>
                    {user?.email && (
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] truncate">
                        {user.email}
                      </p>
                    )}
                    <p className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-wider text-[#8e2a2f] mt-1">
                      {isProMode
                        ? isPendingPro
                          ? 'Professional · Pending approval'
                          : 'Professional'
                        : 'Customer'}
                    </p>
                  </div>
                  <Link
                    to={accountPath}
                    className="flex items-center px-4 py-3 font-['DM_Sans',sans-serif] text-sm font-bold text-[#3a3335] hover:bg-[#fcfaf7] hover:text-[#8e2a2f] transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <span className="material-icons text-lg mr-3">person_outline</span>
                    Profile
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center px-4 py-3 font-['DM_Sans',sans-serif] text-sm font-bold text-[#3a3335] hover:bg-[#fcfaf7] hover:text-[#8e2a2f] transition-colors"
                  >
                    <span className="material-icons text-lg mr-3">logout</span>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Link
                to={signInPath}
                className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] hover:text-[#8e2a2f] transition-colors"
              >
                Sign In
              </Link>
              <Link
                to={signUpPath}
                className="font-['DM_Sans',sans-serif] font-bold bg-[#8e2a2f] hover:bg-[#7a2428] text-white text-[14px] px-5 py-2.5 rounded-[10px] transition-colors"
              >
                {signUpLabel}
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Navigation Menu Overlay */}
        {isMobileMenuOpen && (
          <nav className="absolute top-[80px] left-0 w-full bg-white border-b border-[#ede8e9] py-2 md:hidden z-[60] shadow-xl animate-in slide-in-from-top duration-200">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={handleMobileNavClick}
                  className={`block px-6 py-4 font-['DM_Sans',sans-serif] text-base font-bold transition-colors ${isActive ? 'text-[#8e2a2f] bg-[#fcfaf7]' : 'text-[#3a3335] hover:text-[#8e2a2f] hover:bg-[#fcfaf7]'
                    }`}
                  style={{ fontVariationSettings: "'opsz' 14" }}
                >
                  {link.name}
                  {link.path.endsWith('/messenger') && unreadMessages > 0 ? (
                    <span className="ml-2 inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-[#8e2a2f] text-white text-[10px] font-bold leading-none align-middle">
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  ) : null}
                </Link>
              );
            })}
            {!isAuthenticated && (
              <>
                <div className="h-px bg-[#ede8e9] my-2 mx-6" />
                <Link
                  to={signInPath}
                  onClick={handleMobileNavClick}
                  className="block px-6 py-4 font-['DM_Sans',sans-serif] text-base font-bold text-[#3a3335] hover:text-[#8e2a2f] hover:bg-[#fcfaf7] transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to={signUpPath}
                  onClick={handleMobileNavClick}
                  className="block px-6 py-4 font-['DM_Sans',sans-serif] text-base font-bold text-[#8e2a2f] hover:bg-[#fcfaf7] transition-colors"
                >
                  {signUpLabel}
                </Link>
              </>
            )}
          </nav>
        )}
      </div>

      <ConfirmModal
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={confirmLogout}
        title="Log out?"
        message={
          isProMode
            ? "You'll need to sign in again to access your dashboard."
            : "You'll need to sign in again to view your bookings."
        }
        confirmLabel="Yes, log out"
        cancelLabel="Cancel"
        confirming={loggingOut}
      />
    </header>
  );
}
